import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatMoney, formatNumber, sourceLabel, timeFromNow } from "@/lib/api";
import { SourcePill, StatusTag, ClosingChip } from "@/components/Tags";
import { ArrowUpRight, Gavel, Zap, TrendingUp, Search } from "lucide-react";

const SOURCE_TABS = [
  { key: "all", label: "All" },
  { key: "pickles", label: "Pickles" },
  { key: "slattery", label: "Slattery" },
  { key: "ross", label: "Ross" },
  { key: "ritchiebros", label: "Ritchie Bros" },
];

export default function Dashboard() {
  const [kpi, setKpi] = useState(null);
  const [lots, setLots] = useState([]);
  const [recent, setRecent] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const [source, setSource] = useState("all");
  const [klass, setKlass] = useState("all");
  const [state, setState] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [kpiRes, recentRes, healthRes] = await Promise.all([
        api.get("/dashboard/kpi"),
        api.get("/dashboard/recent-results"),
        api.get("/health"),
      ]);
      setKpi(kpiRes.data);
      setRecent(recentRes.data);
      setHealth(healthRes.data);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const params = {};
    if (source !== "all") params.source = source;
    if (klass !== "all") params.klass = klass;
    if (state !== "all") params.state = state;
    if (status !== "all") params.status = status;
    if (q) params.q = q;
    api.get("/lots", { params }).then((r) => setLots(r.data));
  }, [source, klass, state, status, q]);

  const uniques = useMemo(() => {
    const classes = new Set(), states = new Set();
    lots.forEach((l) => {
      if (l.klass) classes.add(l.klass);
      if (l.state) states.add(l.state);
    });
    return { classes: Array.from(classes).sort(), states: Array.from(states).sort() };
  }, [lots]);

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-[color:var(--caa-charcoal)]">
            Auction dashboard
          </h1>
          <p className="text-sm text-[color:var(--caa-muted)] mt-1">
            Live watch of Australian crane auction lots — bids, closes and sold prices, all in one view.
          </p>
        </div>
      </div>

      {health && (health.overall !== "ok" || !health.ever_crawled) && (
        <HealthBanner health={health} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-row">
        <KpiCard testId="kpi-tracked" icon={<Gavel size={18} />} label="Lots tracked (30d)"
          value={loading ? "…" : formatNumber(kpi?.tracked_30d)} />
        <KpiCard testId="kpi-closed" icon={<Zap size={18} />} label="Closed this week"
          value={loading ? "…" : formatNumber(kpi?.closed_7d)} />
        <KpiCard testId="kpi-live" icon={<TrendingUp size={18} />} label="Live right now"
          value={loading ? "…" : formatNumber(kpi?.live_now)} />
        <KpiCard testId="kpi-topclass" icon={<Gavel size={18} />} label="Top avg by class"
          value={!kpi?.avg_by_class?.length ? "—" :
            `${kpi.avg_by_class[0].klass} · ${formatMoney(kpi.avg_by_class[0].avg)}`}
          hint={kpi?.avg_by_class?.length ? `${kpi.avg_by_class[0].count} sale(s)` : ""} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="caa-card p-4 space-y-3" data-testid="filter-bar">
            <div className="caa-segmented" data-testid="source-segmented">
              {SOURCE_TABS.map((t) => (
                <button key={t.key} data-active={source === t.key}
                  data-testid={`src-tab-${t.key}`} onClick={() => setSource(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <FilterSelect testId="flt-class" label="Class" value={klass} onChange={setKlass}
                options={[{ v: "all", l: "All classes" }, ...uniques.classes.map((c) => ({ v: c, l: c }))]} />
              <FilterSelect testId="flt-state" label="State" value={state} onChange={setState}
                options={[{ v: "all", l: "All states" }, ...uniques.states.map((s) => ({ v: s, l: s }))]} />
              <FilterSelect testId="flt-status" label="Status" value={status} onChange={setStatus}
                options={[{ v: "all", l: "All statuses" }, { v: "open", l: "Live" },
                  { v: "upcoming", l: "Upcoming" }, { v: "sold", l: "Sold" }, { v: "passed", l: "Passed in" }]} />
              <div className="relative flex-1 min-w-[220px]">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--caa-muted)]" />
                <input data-testid="flt-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search title, make, serial…"
                  className="w-full rounded-lg border caa-hairline bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[color:var(--caa-teal-deep)]" />
              </div>
            </div>
          </div>

          <div className="caa-card overflow-hidden" data-testid="lots-table">
            <div className="overflow-x-auto">
              <table className="caa-table">
                <thead>
                  <tr>
                    <th>Machine</th><th>Source</th><th>Location</th><th>Status</th>
                    <th className="text-right">Bid / Price</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-10 text-[color:var(--caa-muted)]">
                      No lots match those filters yet.
                    </td></tr>
                  )}
                  {lots.map((l) => (
                    <tr key={l.id} data-testid={`lot-row-${l.id}`}>
                      <td className="min-w-[260px]">
                        <Link to={`/lots/${l.id}`}
                          className="font-semibold text-[color:var(--caa-charcoal)] hover:text-[color:var(--caa-teal-deep)]">
                          {l.title || `${l.year || ""} ${l.make} ${l.model_name}`.trim()}
                        </Link>
                        <div className="text-xs text-[color:var(--caa-muted)] mt-0.5">
                          {[l.year, l.make, l.model_name].filter(Boolean).join(" · ")}
                          {l.capacity_t ? ` · ${l.capacity_t}T` : ""}
                          {l.hours ? ` · ${formatNumber(l.hours)} hrs` : ""}
                        </div>
                      </td>
                      <td><SourcePill source={l.source} /></td>
                      <td className="text-sm">
                        <div className="text-[color:var(--caa-charcoal)]">{l.location || "—"}</div>
                        <div className="text-xs text-[color:var(--caa-muted)]">{l.state}</div>
                      </td>
                      <td><StatusTag status={l.status} /></td>
                      <td className="text-right caa-num">
                        <div className="font-bold" style={{ color: "var(--caa-teal-deep)" }}>
                          {formatMoney(l.final_bid || l.current_bid)}
                        </div>
                        <div className="text-xs mt-1">
                          {l.status === "open" || l.status === "upcoming" ? (
                            <ClosingChip closingAt={l.closing_at} testId={`closing-chip-${l.id}`} />
                          ) : (
                            <span className="text-[color:var(--caa-muted)]">
                              {l.bid_count ? `${l.bid_count} bids` : ""}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <RecentResults items={recent} />
          <AvgByClass rows={kpi?.avg_by_class || []} />
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, hint, icon, testId }) {
  return (
    <div className="caa-card caa-kpi p-5" data-testid={testId}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
        style={{ background: "var(--caa-teal-soft)", color: "var(--caa-teal-deep)" }}>
        {icon}
      </div>
      <div className="text-xs uppercase tracking-wider font-semibold text-[color:var(--caa-muted)]">{label}</div>
      <div className="text-2xl font-extrabold mt-1 text-[color:var(--caa-charcoal)] caa-num">{value}</div>
      {hint && <div className="text-xs text-[color:var(--caa-muted)] mt-1">{hint}</div>}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="inline-flex items-center gap-2 rounded-lg border caa-hairline bg-white px-2 py-1.5">
      <span className="text-xs text-[color:var(--caa-muted)] font-semibold">{label}</span>
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-sm outline-none pr-1 font-semibold text-[color:var(--caa-charcoal)]">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function RecentResults({ items }) {
  return (
    <div className="caa-card p-5" data-testid="recent-results">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-[color:var(--caa-charcoal)]">Recent results</h3>
        <Link to="/results" className="text-xs font-semibold text-[color:var(--caa-teal-deep)] inline-flex items-center gap-1">
          View all <ArrowUpRight size={12} />
        </Link>
      </div>
      <ul className="space-y-3">
        {items.length === 0 && <li className="text-sm text-[color:var(--caa-muted)]">No recent closes.</li>}
        {items.map((l) => (
          <li key={l.id} className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link to={`/lots/${l.id}`}
                className="text-sm font-semibold text-[color:var(--caa-charcoal)] hover:text-[color:var(--caa-teal-deep)] line-clamp-2">
                {l.title}
              </Link>
              <div className="text-xs text-[color:var(--caa-muted)] mt-0.5">
                {sourceLabel(l.source)} · {timeFromNow(l.actual_close_at || l.closing_at)}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-bold caa-num" style={{ color: "var(--caa-teal-deep)" }}>
                {formatMoney(l.final_bid || l.current_bid)}
              </div>
              <StatusTag status={l.status} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AvgByClass({ rows }) {
  const max = rows.reduce((m, r) => Math.max(m, r.avg), 0) || 1;
  return (
    <div className="caa-card p-5" data-testid="avg-by-class">
      <h3 className="font-bold text-[color:var(--caa-charcoal)] mb-3">Average sale by class</h3>
      {rows.length === 0 && <p className="text-sm text-[color:var(--caa-muted)]">Not enough sold data yet.</p>}
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.klass}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-[color:var(--caa-charcoal)]">{r.klass}</span>
              <span className="caa-num text-[color:var(--caa-muted)]">
                {formatMoney(r.avg)} · {r.count} sale{r.count !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="h-2 rounded-full bg-[color:var(--caa-teal-soft)]/60 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(r.avg / max) * 100}%`, background: "var(--caa-teal-deep)" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function HealthBanner({ health }) {
  // Distinguishes "never crawled" from "crawled, no matches" so an empty table
  // doesn't silently read as "site is quiet today".
  const errCategoryLabel = {
    browser_missing: "browser not installed",
    timeout: "timeout",
    blocked: "blocked by source site",
    rate_limited: "rate-limited",
    network_error: "network error",
    error: "error",
  };
  const isDown = health.overall === "down";
  const neverCrawled = !health.ever_crawled;
  const erroring = health.sources.filter((s) => s.last_error_category);
  const signalling = health.sources.filter((s) => s.signal_reason);

  let title, body, tone;
  if (isDown && !health.playwright_ready) {
    tone = "red";
    title = "Scrapers offline — browser not available";
    body =
      "The headless browser used to fetch auction pages is not installed in this environment. " +
      "No data can be collected until this is fixed on the server.";
  } else if (neverCrawled) {
    tone = "amber";
    title = "No source has completed a crawl yet";
    body =
      "The dashboard is empty because no auction site has been scraped in this environment. " +
      "Trigger a first crawl from the Crawler page or wait for the scheduler to run.";
  } else if (erroring.length) {
    tone = "amber";
    title = `${erroring.length} source${erroring.length > 1 ? "s" : ""} reporting errors`;
    body = erroring
      .map((s) => `${s.name}: ${errCategoryLabel[s.last_error_category] || s.last_error_category}`)
      .join(" · ");
  } else if (signalling.length) {
    // A source that returns nothing and calls it success is indistinguishable
    // from a source that is broken. Flag it explicitly.
    tone = "amber";
    title = `${signalling.length} source${signalling.length > 1 ? "s" : ""} returning suspiciously little`;
    body = signalling
      .map((s) => `${s.name}: ${s.signal_reason} (last kept ${s.lot_count})`)
      .join(" · ") + ". Site may be between auction cycles, or the scraper may need updating — check Crawler page.";
  } else {
    return null;
  }

  const bg = tone === "red" ? "bg-red-50 border-red-200 text-red-900"
                            : "bg-amber-50 border-amber-200 text-amber-900";
  return (
    <div
      data-testid="health-banner"
      className={`caa-card border ${bg} p-4 flex items-start gap-3`}
    >
      <div className="flex-1">
        <div className="font-bold text-sm">{title}</div>
        <div className="text-sm mt-1 opacity-90">{body}</div>
      </div>
      <Link
        to="/crawler"
        data-testid="health-banner-crawler-link"
        className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold bg-white border border-current hover:bg-white/60"
      >
        Open Crawler
      </Link>
    </div>
  );
}
